import React, { useMemo } from 'react';
import { useAppContext } from '@prisjakt/ui-app-context';
import { H3Text } from '@prisjakt/ui/text/variants';
import { Product, KeyWordLandingPageCategory } from '../../../types';
import { OffersGrid } from '../../product-collection/components/offers-grid';

import { ProductCardWrapper } from './product-card-wrapper';
import { mergeProductsWithCategories } from './utils';
import { ProductSection } from './product-section';
import * as S from './styles';

type ProductLayoutProps = {
  products: Product[];
  categories: KeyWordLandingPageCategory[];
  onClick?: (p: Product) => void;
};

const MIN_PRODUCT_COUNT = 2;

export const ProductLayout = ({
  products,
  categories,
  onClick,
}: ProductLayoutProps) => {
  const { t } = useAppContext();
  const mergedProductsWithCategories = useMemo(
    () => mergeProductsWithCategories(products, categories),
    [products, categories]
  );

  const allProductsWithLessThenThree = useMemo(
    () =>
      mergedProductsWithCategories
        .filter(l => l.products.length <= MIN_PRODUCT_COUNT)
        .reduce((acc, c) => [...acc, ...c.products], [] as Product[]),
    [mergedProductsWithCategories]
  );
  // There is only one category
  if (mergedProductsWithCategories.length === 1) {
    return (
      <S.ProductSection>
        <H3Text>{mergedProductsWithCategories[0].name}</H3Text>
        <OffersGrid
          offers={mergedProductsWithCategories[0].products.map(p => (
            <ProductCardWrapper key={p.id} product={p} onClick={onClick} />
          ))}
        />
      </S.ProductSection>
    );
  }
  return (
    <>
      {mergedProductsWithCategories
        .filter(l => l.products.length > MIN_PRODUCT_COUNT)
        .map(({ classicId, name, products: mergedProducts, url }) => (
          <ProductSection
            key={classicId}
            id={classicId}
            title={name}
            products={mergedProducts}
            link={url}
            onClick={onClick}
          />
        ))}
      {allProductsWithLessThenThree.length > 0 && (
        <ProductSection
          key="other-products"
          id="other-products"
          title={t('category', 'keyword-landing-page.other-categories-title')}
          products={allProductsWithLessThenThree}
          onClick={onClick}
        />
      )}
    </>
  );
};
